import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';
import { Toaster } from 'sonner';
import Landing from './pages/Landing';
import Index from './pages/Index';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import Releases from './pages/Releases';
import ReleaseEdit from './pages/ReleaseEdit';
import Library from './pages/Library';
import MusiciansNetwork from './pages/MusiciansNetwork';
import Analytics from './pages/Analytics';
import Marketing from './pages/Marketing';
import Marketplace from './pages/Marketplace';
import Learning from './pages/Learning';
import Royalties from './pages/Royalties';
import MusicRightsAdmin from './pages/MusicRightsAdmin';
import LiveStreaming from './pages/LiveStreaming';
import LegalTools from './pages/LegalTools';
import GigManagement from './pages/GigManagement';
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Collaboration from './pages/Collaboration';
import ProjectDetails from './pages/ProjectDetails';
import LiveSession from './pages/LiveSession';
import Radio from './pages/Radio';
import ArtistProgress from './pages/Gamification';
import Sync from './pages/Sync';
import Merchandise from './pages/Merchandise';
import RegionalMarketing from './pages/RegionalMarketing';
import Charts from './pages/Charts';
import Funding from './pages/Funding';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import AwardNetworking from '@/pages/AwardNetworking';
import CalendarScheduling from '@/pages/CalendarScheduling';
import AIAssistant from '@/components/assistant/AIAssistant';
import { VoiceCommandProvider } from '@/contexts/VoiceCommandContext';
import VoiceCommandButton from '@/components/voice/VoiceCommandButton';
import VoiceCommandList from '@/components/voice/VoiceCommandList';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const BASE_PATH = '/';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Auth check error:", error);
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(!!data.session);
        }
      } catch (e) {
        console.error("Auth check failed:", e);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });
    
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3">Verifying access...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/signin" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  const [session, setSession] = useState<Session | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();
  
  const isAuthPage = location.pathname.startsWith('/auth');
  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    console.log('Current path:', location.pathname);
    
    const initializeAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setSession(data.session);
      } catch (error) {
        console.error("Error getting session:", error);
      } finally {
        setIsInitialized(true);
      }
    };
    
    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [location.pathname]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3">Loading app...</span>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={session ? <Navigate to="/dashboard" replace /> : <Landing />} />
        <Route path="/home" element={<ProtectedRoute><Index /></ProtectedRoute>} />
        
        <Route path="/auth/signin" element={<Auth />} />
        <Route path="/auth/signup" element={<Auth />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/*" element={<Auth />} />
        
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

        <Route path="/releases" element={<ProtectedRoute><Releases /></ProtectedRoute>} />
        <Route path="/releases/:releaseId" element={<ProtectedRoute><ReleaseEdit /></ProtectedRoute>} />
        <Route path="/releases/distribution" element={<ProtectedRoute><Navigate to="/releases?tab=distribution" replace /></ProtectedRoute>} />
        
        <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
        
        <Route path="/musicians-network" element={<ProtectedRoute><MusiciansNetwork /></ProtectedRoute>} />
        <Route path="/network" element={<ProtectedRoute><Navigate to="/musicians-network" replace /></ProtectedRoute>} />
        <Route path="/match" element={<ProtectedRoute><Navigate to="/musicians-network" replace /></ProtectedRoute>} />
        <Route path="/musicians-match" element={<ProtectedRoute><Navigate to="/musicians-network" replace /></ProtectedRoute>} />
        
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/marketing" element={<ProtectedRoute><Marketing /></ProtectedRoute>} />
        <Route path="/marketing/region/:region" element={<ProtectedRoute><RegionalMarketing /></ProtectedRoute>} />
        <Route path="/charts" element={<ProtectedRoute><Charts /></ProtectedRoute>} />
        <Route path="/sync" element={<ProtectedRoute><Sync /></ProtectedRoute>} />
        
        <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
        <Route path="/merchandise" element={<ProtectedRoute><Merchandise /></ProtectedRoute>} />
        <Route path="/news" element={<ProtectedRoute><Navigate to="/learning?tab=news" replace /></ProtectedRoute>} />
        
        <Route path="/royalties" element={<ProtectedRoute><Royalties /></ProtectedRoute>} />
        <Route path="/radio" element={<ProtectedRoute><Radio /></ProtectedRoute>} />
        <Route path="/music-rights-admin" element={<ProtectedRoute><MusicRightsAdmin /></ProtectedRoute>} />
        <Route path="/rights-admin" element={<ProtectedRoute><Navigate to="/music-rights-admin" replace /></ProtectedRoute>} />
        
        <Route path="/live-streaming" element={<ProtectedRoute><LiveStreaming /></ProtectedRoute>} />
        
        <Route path="/legal-tools" element={<ProtectedRoute><LegalTools /></ProtectedRoute>} />
        <Route path="/gig-management" element={<ProtectedRoute><GigManagement /></ProtectedRoute>} />
        <Route path="/gigs" element={<ProtectedRoute><Navigate to="/gig-management" replace /></ProtectedRoute>} />
        <Route path="/funding" element={<ProtectedRoute><Funding /></ProtectedRoute>} />
        
        <Route path="/settings/*" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        
        <Route path="/collaboration" element={<ProtectedRoute><Collaboration /></ProtectedRoute>} />
        <Route path="/collaboration/projects/:projectId" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
        <Route path="/collaboration/sessions/:sessionId" element={<ProtectedRoute><LiveSession /></ProtectedRoute>} />
        
        <Route path="/artist-progress" element={<ProtectedRoute><ArtistProgress /></ProtectedRoute>} />
        <Route path="/gamification" element={<ProtectedRoute><Navigate to="/artist-progress" replace /></ProtectedRoute>} />
        <Route path="/artist-health" element={<ProtectedRoute><Navigate to="/artist-progress?tab=health" replace /></ProtectedRoute>} />
        
        <Route path="/learning" element={<ProtectedRoute><Learning /></ProtectedRoute>} />
        
        <Route path="/award-networking" element={<ProtectedRoute><AwardNetworking /></ProtectedRoute>} />
        <Route path="/calendar-scheduling" element={<ProtectedRoute><CalendarScheduling /></ProtectedRoute>} />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
      
      {session && <AIAssistant />}
      
      {session && !isAuthPage && !isLandingPage && (
        <div className="fixed bottom-4 left-4 z-40">
          <VoiceCommandButton />
        </div>
      )}
      
      <VoiceCommandList />
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Unhandled error:', event.error);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router basename={BASE_PATH}>
        <TooltipProvider>
          <VoiceCommandProvider>
            <AppContent />
          </VoiceCommandProvider>
        </TooltipProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
